/*
 * BuroEcl
 * Copyright (c) 2023 Buroclic. All rights reserved.
 * 
 * This file corresponds to the form in order to edit or create
 * a cabinet's configuration from the administration panel
 */

// Imports
import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Autocomplete, Switch, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";

// Interfaces
import { Cabinet, ListedCabinet, UpdatingCabinet } from "../../interfaces/ICabinets";
import { AlertType } from "../../interfaces/IUtils";

// Utils
import { GlobalApiCall, HTTPResponse } from "../utils/ApiCallConstructor";
import { ErrorForUsr, displayAlert } from "../utils/Errors";

// Buroclic adaptive components
import { PopUp } from "../buroclic/popUp";

// This form is use whether it is an edit or a creation
export const AdminCabinetForm = (props) => {
    const navigate = useNavigate();
    const id = props.cabinetId;
    const [cabinet, setCabinet] = useState<Cabinet>({} as Cabinet);

    const [isOpen, setIsOpen] = useState<boolean>(props.visible || false);

    // states
    const [logo, setLogo] = useState<string>(''); // Row 1 : logo
    const [cabinetAlias, setCabinetAlias] = useState<string>(''); // Row 2 : cabinetAlias
    const [cabinetName, setCabinetName] = useState<string>(''); // Row 2 : cabinetName
    const [host, setHost] = useState<string>(''); // Row 3 : Host
    const [port, setPort] = useState<number>(443); // Row 3 : Port
    const [dbName, setDbName] = useState<string>(''); // Row 4 : dbName
    //const [cfgFile, setCfgFile] = useState<string>(''); // Row 4 : cfgFile
    const [cabinetFeatures, setCabinetFeatures] = useState<string[]>([]); // Row 4 : Features
    const [banner, setBanner] = useState<string>(''); // Row 5 : Banner
    const [bannerUrl, setBannerUrl] = useState<string>(''); // Row 5 : Banner URL
    const [advert1, setAdvert1] = useState<string>(''); // Row 6 : Ad 1
    const [advert1Url, setAdvert1Url] = useState<string>(''); // Row 6 : Ad 1
    const [advert2, setAdvert2] = useState<string>(''); // Row 7 : Ad 2
    const [advert2Url, setAdvert2Url] = useState<string>(''); // Row 7 : Ad 2
    const [advert3, setAdvert3] = useState<string>(''); // Row 8 : Ad 3
    const [advert3Url, setAdvert3Url] = useState<string>(''); // Row 8 : Ad 3
    const [color, setColor] = useState<string>('#E7812B'); // Row 9 : Color
    const [loginBackground, setLoginBackground] = useState<string>(''); // Row 10 : login Background
    const [isActive, setIsActive] = useState<boolean>(true); // Row 11 : isActive

    const availableFeatures: string[] = ['BuroSecure', 'Planning'];

    // Initial data
    useEffect(() => {
        // get cabinet data if the user is editing something
        const fetchCabinet = async () => {
            try {
                const cabinetResponse = await toast.promise(
                    GlobalApiCall({
                        endpoint: `/admin/cabinets/${id}`,
                        method: 'GET',
                    }),
                    {
                        pending: 'Récupération de la configuration du cabinet en cours ...',
                        error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } }
                    });

                setCabinet(cabinetResponse.result as Cabinet);
            } catch (err: any) {
                // Handle redirection
                if (err.error === ErrorForUsr.NotSignedIn) {
                    localStorage.clear();
                    navigate('/admin/login');
                }
                console.error(err.error)
            }
        }

        if (id > 0)
            fetchCabinet();
    }, [navigate, id]);


    // Update every states when cabinet data has been loaded
    useEffect(() => {
        if (cabinet.id) {
            setCabinetAlias(cabinet.folder);
            setCabinetName(cabinet.title);
            setLogo(cabinet.logo || '');
            setIsActive(cabinet.enabled);
            setHost(cabinet.host);
            //setCfgFile(cabinet.cfg);
            setPort(cabinet.port || 443);
            setDbName(cabinet.db);

            // Features
            let tmpCabinetFeatures: string[] = [];
            if (!!cabinet.is_burosecure_enabled === true) tmpCabinetFeatures.push('BuroSecure');
            if (!!cabinet.is_planning_enabled === true) tmpCabinetFeatures.push('Planning');
            console.log(tmpCabinetFeatures);
            setCabinetFeatures(tmpCabinetFeatures);

            setBanner(cabinet.banner || '');
            setBannerUrl(cabinet.banner_url || '');
            setAdvert1(cabinet.pub1 || '');
            setAdvert1Url(cabinet.pub1_url || '');
            setAdvert2(cabinet.pub2 || '');
            setAdvert2Url(cabinet.pub2_url || '');
            setAdvert3(cabinet.pub3 || '');
            setAdvert3Url(cabinet.pub3_url || '');
            setLoginBackground(cabinet.login_background || '');
            setColor(cabinet.font_color || '#000000');
        }
    }, [cabinet]);

    const handleFeatureChange = (event, newValue) => {
        const clickedValue = event.target.getAttribute('data-feature');
        if (!cabinetFeatures.includes(clickedValue)) setCabinetFeatures([...cabinetFeatures, clickedValue]);
        else {
            const updatedFeatures = cabinetFeatures.filter(feature => feature !== clickedValue);
            setCabinetFeatures(updatedFeatures);
        }
    }

    // Checks whether or not the mandatory fields off the form has been filled in
    const isFormValid = (): boolean => {

        // cabinetAlias
        if (cabinetAlias.length <= 0 || /[^a-zA-Z0-9-]/im.test(cabinetAlias)) {
            displayAlert({ message: `L'Alias du cabinet est manquant ou contient des caractères interdits`, type: AlertType.error });
            return false;
        }

        // cabinetName
        if (cabinetName.length <= 0) {
            displayAlert({ message: `Le nom du cabinet est incorrect`, type: AlertType.error });
            return false;
        }

        // IP
        if (host.length <= 0 || (!(/^[0-9]+\.[0-9]+\.[0-9]+\.[0-9]+$/i.test(host)) && !(/^[a-zA-Z0-9-_]+\.buroclic\.com$/i.test(host)))) {
            displayAlert({ message: `Le nom de domaine du cabinet est manquant ou incorrecte`, type: AlertType.error });
            return false;
        }

        if (port <= 0) {
            displayAlert({ message: `Le port est manquant ou incorrect`, type: AlertType.error });
            return false;
        }

        // CONFIG FILE
        /*if (cfgFile.length <= 0 || !(/^[\w,\s-]+\.[A-Za-z0-9]+$/.test(cfgFile))) {
            displayAlert({ message: `Le nom du fichier de configuration est incorrect`, type: AlertType.error });
            return false;
        }*/

        // DB NAME
        if (dbName.length <= 0) {
            displayAlert({ message: `L'alias de la base de données est incorrect`, type: AlertType.error });
            return false;
        }

        return true;
    }


    // Save the current form into the Database as a new cabinet record or edit the matching one
    const saveCabinet = async (e) => {
        e.preventDefault();

        if (!isFormValid())
            return;

        // Create a cabinet object with mandatory fields
        const savedCabinet: UpdatingCabinet = {
            cabinetAlias: cabinetAlias,
            cabinetName: cabinetName,
            logo: logo,
            isActive: isActive,
            host: host,
            dbName: dbName,
            port: port,
            isBurosecureEnabled: cabinetFeatures.includes('BuroSecure'),
            isPlanningEnabled: cabinetFeatures.includes('Planning')
        }

        if (id > 0) savedCabinet.id = id; // optional ID (for edit only)
        if (advert1 !== '') savedCabinet.advertisement1 = advert1; // optional advertisement
        if (advert1Url !== '') savedCabinet.advertisement1Url = advert1Url; // optional advertisement redirection
        if (advert2 !== '') savedCabinet.advertisement2 = advert2; // optional advertisement
        if (advert2Url !== '') savedCabinet.advertisement2Url = advert2Url; // optional advertisement redirection
        if (advert3 !== '') savedCabinet.advertisement3 = advert3; // optional advertisement
        if (advert3Url !== '') savedCabinet.advertisement3Url = advert3Url; // optional advertisement redirection
        if (banner !== '') savedCabinet.banner = banner; // optional banner
        if (bannerUrl !== '') savedCabinet.bannerUrl = bannerUrl; // optional banner redirection
        if (loginBackground !== '') savedCabinet.loginBackground = loginBackground; // optional login background
        if (color !== '') savedCabinet.color = color; // color

        console.log(savedCabinet);

        try {
            await toast.promise(
                GlobalApiCall({
                    endpoint: `/admin/cabinets`,
                    method: 'PUT',
                    body: savedCabinet
                }),
                {
                    pending: 'Sauvegarde de la nouvelle configuration du cabinet en cours ...',
                    error: { render({ data }) { return `Erreur : ${(data as HTTPResponse).error}` } },
                    success: 'Configuration sauvegardée'
                });

            setIsOpen(false);
            props.setVisible(false);
            props.setCabinet({} as ListedCabinet);
            props.setToRefresh(true);
        } catch (err: any) {
            // Handle redirection
            if (err.error === ErrorForUsr.NotSignedIn) {
                localStorage.clear();
                navigate('/admin/login');
            }
            console.error(err.error)
        }
    }


    // Update the advertisement image content and its preview
    const loadAdvertisement = async (index: number) => {
        let files;
        let base64Content: any = '';

        if (index === 0)
            files = (document.getElementById('logo') as HTMLInputElement).files;
        else if (index === 98)
            files = (document.getElementById('loginBackground') as HTMLInputElement).files;
        else if (index === 99)
            files = (document.getElementById('banner') as HTMLInputElement).files;
        else
            files = (document.getElementById(`ad${index}img`) as HTMLInputElement).files

        if (files && files.length > 0) {
            const file = files[0];
            const reader = new FileReader();
            reader.readAsDataURL(file);
            base64Content = await new Promise((resolve, reject) => {
                reader.onload = function (e) {
                    resolve(e.target?.result);
                }
            });
        }

        switch (index) {
            case 0:
                setLogo(base64Content);
                break;
            case 1:
                setAdvert1(base64Content);
                break;
            case 2:
                setAdvert2(base64Content);
                break;
            case 3:
                setAdvert3(base64Content);
                break;
            case 98:
                setLoginBackground(base64Content);
                break;
            case 99:
                setBanner(base64Content);
                break;
            default:
                break;
        }
    }

    // UI Functions

    const getFileImg = (index: number) => {
        const deleteFileImg = (index: number) => {
            switch (index) {
                case 0:
                    (document.getElementById('logo') as HTMLInputElement).value = '';
                    setLogo('');
                    break;
                case 1:
                    (document.getElementById('ad1img') as HTMLInputElement).value = '';
                    setAdvert1('');
                    break;
                case 2:
                    (document.getElementById('ad2img') as HTMLInputElement).value = '';
                    setAdvert2('');
                    break
                case 3:
                    (document.getElementById('ad3img') as HTMLInputElement).value = '';
                    setAdvert3('');
                    break;
                case 98:
                    (document.getElementById('loginBackground') as HTMLInputElement).value = '';
                    setLoginBackground('');
                    break;
                case 99:
                    (document.getElementById('banner') as HTMLInputElement).value = '';
                    setBanner('');
                    break;
                default:
                    break;
            }
        }

        // Only preview img if this is an edit
        switch (true) {
            case (index < 98):
                const tmp = [logo, advert1, advert2, advert3];
                if (tmp[index] !== '') {
                    try {
                        return (
                            <div className={`w-full flex flex-row}`} >
                                {index === 0 || index === 99 ? <img src={tmp[index]} alt={`Logo`} className="h-full w-full object-contain object-left max-h-[20vh] max-w-[90%] my-2" />
                                    : <img src={tmp[index]} alt={`Publicité ${index}`} className="h-full w-full object-contain object-left max-h-[20vh] max-w-[90%] my-2" />}
                                <FontAwesomeIcon onClick={() => deleteFileImg(index)} className="w-12 h-12 text-red-500 cursor-pointer" icon={faClose} />
                            </div>
                        );
                    } catch (err) { };
                }
                break;
            case (index === 98):
                if (loginBackground !== '')
                    // loginBackground
                    return (
                        <div className="w-full flex flex-row">
                            <img src={loginBackground} alt="login background" className="h-full w-full object-contain object-left max-h-[20vh] max-w-[90%] my-2" />
                            <FontAwesomeIcon onClick={() => deleteFileImg(98)} className="w-12 h-12 text-red-500 cursor-pointer" icon={faClose} />
                        </div>);
                break;
            case (index === 99):
                if (banner !== '')
                    // Banner
                    return (
                        <div className="w-full flex flex-row">
                            <img src={banner} alt="banner" className="h-full w-full object-contain object-left max-h-[20vh] max-w-[90%] my-2" />
                            <FontAwesomeIcon onClick={() => deleteFileImg(99)} className="w-12 h-12 text-red-500 cursor-pointer" icon={faClose} />
                        </div>);
                break;
            default:
                break;
        }
        return <></>;
    }

    return (

        <PopUp key={`edit-${String(isOpen)}-${props.cabinetId ? props.cabinetId : 0}`} title="Ajouter / Modifier un espace client" className="popup-center"
            visible={isOpen} modal
            closeHandler={() => { setIsOpen(false); props.setVisible(false); props.setCabinet({} as ListedCabinet) }}
            confirmLabel="Sauvegarder" confirmHandler={saveCabinet}>
            <form onSubmit={saveCabinet} className="cabinet-form flex flex-col w-full">

                {/* Row 1 : Logo */}
                <div className="my-2">
                    <label htmlFor="logo" className="text-gray-500 font-semibold hidden lg:inline-block">
                        Logo :
                    </label>
                    {getFileImg(0)}
                    <input tabIndex={4} type="file" id="logo" onChange={(e) => { loadAdvertisement(0) }} accept="image/png"
                        className="relative h-10 m-0 block w-full mb-1 min-w-0 flex-auto rounded border border-solid border-neutral-300 bg-clip-padding px-3 py-[0.32rem] text-base font-normal text-neutral-700 transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:overflow-hidden file:rounded-none file:border-0 file:border-solid file:border-inherit file:bg-neutral-100 file:px-3 file:py-[0.32rem] file:text-neutral-700 file:transition file:duration-150 file:ease-in-out file:[border-inline-end-width:1px] file:[margin-inline-end:0.75rem] hover:file:bg-neutral-200 focus:border-primary focus:text-neutral-700 focus:shadow-te-primary focus:outline-none dark:border-neutral-600 dark:text-neutral-400 dark:file:rounded dark:file:ml-[-1rem] dark:file:bg-blue-400 dark:file:h-10 dark:file:text-neutral-100 dark:focus:border-primary" />
                </div>

                <div className="flex flex-row">

                    {/* Left column */}
                    <div className="flex flex-col w-full mr-4">

                        {/* Row 2 : Alias */}
                        <div className="mb-2">
                            <label htmlFor="cabinetAlias" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Alias : <span data-title="Exclusivement des lettres, chiffres et tirets"><FontAwesomeIcon className="w-4 h-4 text-blue-400" icon={faQuestionCircle} /></span>
                            </label>
                            <input tabIndex={5} type="text" id="cabinetAlias" value={cabinetAlias} onChange={(e) => setCabinetAlias(e.target.value)} className="border border-gray-300 bg-white py-2 px-3 w-full rounded-md h-10 mb-1 lg:mb-3 shadow-bottom" />
                            <span className={`italic text-sm ${cabinetAlias === '' ? 'hidden' : ''}`}>URL supposé : <span className="text-blue-500">{window.location.origin}/{cabinetAlias}/login</span></span>
                        </div>

                        {/* Row 3 : Host */}
                        <div className="my-2">
                            <label htmlFor="host" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Nom de domaine :
                            </label>
                            <input tabIndex={7} type="text" id="host" value={host} onChange={(e) => setHost(e.target.value)} className="border border-gray-300 bg-white py-2 px-3 w-full rounded-md h-10 mb-1 lg:mb-3 shadow-bottom" />
                        </div>

                        {/* Row 4 : Database Name*/}
                        <div className="my-2">
                            <label htmlFor="dbName" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Base De Données : <span data-title="Alias de la BDD dans le fichier de config."><FontAwesomeIcon className="w-4 h-4 text-blue-400" icon={faQuestionCircle} /></span>
                            </label>
                            <input tabIndex={9} type="text" id="dbName" value={dbName} onChange={(e) => setDbName(e.target.value)} className="border border-gray-300 bg-white py-2 px-3 w-full rounded-md h-10 mb-1 lg:mb-3 shadow-bottom" />
                        </div>

                        {/* cfgFile */}
                        {/*<div className="my-2">
                            <label htmlFor="cfgFile" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Fichier de config. : <span data-title="Fichier .cfg se trouvant dans le dossier d'installation de BuroClic"><FontAwesomeIcon className="w-4 h-4 text-blue-400" icon={faQuestionCircle} /></span>
                            </label>
                            <input type="text" id="cfgFile" value={cfgFile} onChange={(e) => setCfgFile(e.target.value)} className="border border-gray-300 bg-white py-2 px-3 w-full rounded-md h-10 mb-1 lg:mb-3 shadow-bottom" />
                        </div>*/}

                        {/* Row 5 : Banner */}
                        <div className="my-2">
                            <label htmlFor="banner" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Bannière :
                            </label>
                            {getFileImg(99)}
                            <input tabIndex={11} type="file" id="banner" onChange={(e) => { loadAdvertisement(99) }} accept="image/*"
                                className="relative h-10 m-0 block w-full mb-1 min-w-0 flex-auto rounded border border-solid border-neutral-300 bg-clip-padding px-3 py-[0.32rem] text-base font-normal text-neutral-700 transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:overflow-hidden file:rounded-none file:border-0 file:border-solid file:border-inherit file:bg-neutral-100 file:px-3 file:py-[0.32rem] file:text-neutral-700 file:transition file:duration-150 file:ease-in-out file:[border-inline-end-width:1px] file:[margin-inline-end:0.75rem] hover:file:bg-neutral-200 focus:border-primary focus:text-neutral-700 focus:shadow-te-primary focus:outline-none dark:border-neutral-600 dark:text-neutral-400 dark:file:rounded dark:file:ml-[-1rem] dark:file:bg-blue-400 dark:file:h-10 dark:file:text-neutral-100 dark:focus:border-primary" />
                        </div>


                        {/* Row 6 : Advertisement 1 */}
                        <div className="my-2">
                            <label htmlFor="ad1img" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Publicité n°1 :
                            </label>
                            {getFileImg(1)}
                            <input tabIndex={13} type="file" id="ad1img" onChange={(e) => { if (e.target.files && e.target.files.length > 0) loadAdvertisement(1) }} accept="image/*"
                                className="relative h-10 m-0 block w-full mb-1 min-w-0 flex-auto rounded border border-solid border-neutral-300 bg-clip-padding px-3 py-[0.32rem] text-base font-normal text-neutral-700 transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:overflow-hidden file:rounded-none file:border-0 file:border-solid file:border-inherit file:bg-neutral-100 file:px-3 file:py-[0.32rem] file:text-neutral-700 file:transition file:duration-150 file:ease-in-out file:[border-inline-end-width:1px] file:[margin-inline-end:0.75rem] hover:file:bg-neutral-200 focus:border-primary focus:text-neutral-700 focus:shadow-te-primary focus:outline-none dark:border-neutral-600 dark:text-neutral-400 dark:file:rounded dark:file:ml-[-1rem] dark:file:bg-blue-400 dark:file:h-10 dark:file:text-neutral-100 dark:focus:border-primary" />
                        </div>

                        {/* Row 7 : Advertisement 2 */}
                        <div className="my-2">
                            <label htmlFor="ad2img" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Publicité n°2 :
                            </label>
                            {getFileImg(2)}
                            <input tabIndex={15} type="file" id="ad2img" onChange={(e) => { if (e.target.files && e.target.files.length > 0) loadAdvertisement(2) }} accept="image/*"
                                className="relative h-10 m-0 block w-full mb-1 min-w-0 flex-auto rounded border border-solid border-neutral-300 bg-clip-padding px-3 py-[0.32rem] text-base font-normal text-neutral-700 transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:overflow-hidden file:rounded-none file:border-0 file:border-solid file:border-inherit file:bg-neutral-100 file:px-3 file:py-[0.32rem] file:text-neutral-700 file:transition file:duration-150 file:ease-in-out file:[border-inline-end-width:1px] file:[margin-inline-end:0.75rem] hover:file:bg-neutral-200 focus:border-primary focus:text-neutral-700 focus:shadow-te-primary focus:outline-none dark:border-neutral-600 dark:text-neutral-400 dark:file:rounded dark:file:ml-[-1rem] dark:file:bg-blue-400 dark:file:h-10 dark:file:text-neutral-100 dark:focus:border-primary" />
                        </div>

                        {/* Row 8 : Advertisement 3 */}
                        <div className="my-2">
                            <label htmlFor="ad3img" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Publicité n°3 :
                            </label>
                            {getFileImg(3)}
                            <input tabIndex={17} type="file" id="ad3img" onChange={(e) => { if (e.target.files && e.target.files.length > 0) loadAdvertisement(3) }} accept="image/*"
                                className="relative h-10 m-0 block w-full mb-1 min-w-0 flex-auto rounded border border-solid border-neutral-300 bg-clip-padding px-3 py-[0.32rem] text-base font-normal text-neutral-700 transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:overflow-hidden file:rounded-none file:border-0 file:border-solid file:border-inherit file:bg-neutral-100 file:px-3 file:py-[0.32rem] file:text-neutral-700 file:transition file:duration-150 file:ease-in-out file:[border-inline-end-width:1px] file:[margin-inline-end:0.75rem] hover:file:bg-neutral-200 focus:border-primary focus:text-neutral-700 focus:shadow-te-primary focus:outline-none dark:border-neutral-600 dark:text-neutral-400 dark:file:rounded dark:file:ml-[-1rem] dark:file:bg-blue-400 dark:file:h-10 dark:file:text-neutral-100 dark:focus:border-primary" />
                        </div>


                        {/* Row 9 : Color */}
                        <div className="mb-2 flex flex-col">
                            <label htmlFor="color" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Couleur :
                            </label>
                            <div className="flex flex-row">
                                <input tabIndex={19} type="color" id="color" value={color} onChange={(e) => setColor(e.target.value)} className="border cursor-pointer border-gray-300 bg-white w-10 rounded-md h-10 shadow-bottom" />
                                <input type="text" id="colorValue" value={color} onChange={(e) => setColor(e.target.value)} className="border border-gray-300 bg-white ml-2 py-2 px-2 w-full rounded-md h-10 mb-1 lg:mb-3 shadow-bottom" />
                            </div>
                        </div>

                    </div>



                    {/* Right column */}
                    <div className="flex flex-col w-full ml-4">

                        {/* Row 2 : Name */}
                        <div className="mb-2">
                            <label htmlFor="cabinetName" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Nom : <span data-title="Uniquement visible aux administrateurs"><FontAwesomeIcon className="w-4 h-4 text-blue-400" icon={faQuestionCircle} /></span>
                            </label>
                            <input tabIndex={6} type="text" id="cabinetName" value={cabinetName} onChange={(e) => setCabinetName(e.target.value)} className="border border-gray-300 bg-white py-2 px-3 w-full rounded-md h-10 mb-1 lg:mb-3 shadow-bottom" />
                            <span className={`italic text-sm invisible ${cabinetAlias === '' ? 'hidden' : ''}`}>&nbsp;</span>
                        </div>

                        {/* Row 3 : Port */}
                        <div className={`my-2`}>
                            <label htmlFor="port" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Port :
                            </label>
                            <input tabIndex={8} type="number" step={1} id="port" value={port} onChange={(e) => setPort(Number(e.target.value))} className="border border-gray-300 bg-white py-2 px-3 w-full rounded-md h-10 mb-1 lg:mb-3 shadow-bottom" />
                        </div>


                        {/* Row 4 : Cabinet Features */}
                        <div className="my-2">
                            <label htmlFor="cabinetFeatures" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Fonctionnalités :
                            </label>
                            <Autocomplete key={'autocomplete-' + cabinetFeatures.length} multiple id="cabinetFeatures" value={cabinetFeatures} disableCloseOnSelect disableClearable size="small" getOptionLabel={(option) => option} style={{ height: '2.5rem' }}
                                onChange={handleFeatureChange}
                                renderInput={(params) => (<TextField key={'input-' + cabinetFeatures.length} {...params} placeholder="Fonctionnalités" />)}
                                renderOption={(props, option, { selected }) => (<li {...props} data-feature={option}><input readOnly type="checkbox" className="mr-2" checked={selected} />{option}</li>)}
                                options={availableFeatures} />

                            {/*<input tabIndex={10} type="text" id="cabinetFeatures" value={cabinetFeatures} onChange={(e) => setCabinetFeatures([])} className="border border-gray-300 bg-white py-2 px-3 w-full rounded-md h-10 mb-1 lg:mb-3 shadow-bottom" />*/}
                        </div>

                        {/* Row 5 : Banner */}
                        <div className="my-2">
                            <div className="invisible">{getFileImg(99)}</div>

                            <label htmlFor="bannerUrl" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Redirection bannière :
                            </label>

                            <input tabIndex={12} type="text" id="bannerUrl" value={bannerUrl} onChange={(e) => setBannerUrl(e.target.value)} className="border border-gray-300 bg-white py-2 px-3 w-full rounded-md h-10 mb-1 lg:mb-3 shadow-bottom" />
                        </div>

                        {/* Row 6 : Advertisement 1 */}
                        <div className="my-2">
                            <div className="invisible">{getFileImg(1)}</div>

                            <label htmlFor="ad1url" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Redirection publicité n°1 :
                            </label>

                            <input tabIndex={14} type="text" id="ad1url" value={advert1Url} onChange={(e) => setAdvert1Url(e.target.value)} className="border border-gray-300 bg-white py-2 px-3 w-full rounded-md h-10 mb-1 lg:mb-3 shadow-bottom" />
                        </div>

                        {/* Row 7 : Advertisement 2 */}
                        <div className="my-2">
                            <div className="invisible">{getFileImg(2)}</div>

                            <label htmlFor="ad2url" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Redirection publicité n°2 :
                            </label>

                            <input tabIndex={16} type="text" id="ad2url" value={advert2Url} onChange={(e) => setAdvert2Url(e.target.value)} className="border border-gray-300 bg-white py-2 px-3 w-full rounded-md h-10 mb-1 lg:mb-3 shadow-bottom" />
                        </div>

                        {/* Row 8 : Advertisement 3 */}
                        <div className="my-2">
                            <div className="invisible">{getFileImg(3)}</div>

                            <label htmlFor="ad3url" className="text-gray-500 font-semibold hidden lg:inline-block">
                                Redirection publicité n°3 :
                            </label>

                            <input tabIndex={18} type="text" id="ad3url" value={advert3Url} onChange={(e) => setAdvert3Url(e.target.value)} className="border border-gray-300 bg-white py-2 px-3 w-full rounded-md h-10 mb-1 lg:mb-3 shadow-bottom" />
                        </div>
                    </div>


                </div>

                {/* Login Background */}
                <div className="my-2">
                    <label htmlFor="loginBackground" className="text-gray-500 font-semibold hidden lg:inline-block">
                        Background de la page de connexion :
                    </label>
                    {getFileImg(98)}
                    <input tabIndex={20} type="file" id="loginBackground" onChange={(e) => { loadAdvertisement(98) }} accept="image/*"
                        className="relative h-10 m-0 block w-full mb-1 min-w-0 flex-auto rounded border border-solid border-neutral-300 bg-clip-padding px-3 py-[0.32rem] text-base font-normal text-neutral-700 transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:overflow-hidden file:rounded-none file:border-0 file:border-solid file:border-inherit file:bg-neutral-100 file:px-3 file:py-[0.32rem] file:text-neutral-700 file:transition file:duration-150 file:ease-in-out file:[border-inline-end-width:1px] file:[margin-inline-end:0.75rem] hover:file:bg-neutral-200 focus:border-primary focus:text-neutral-700 focus:shadow-te-primary focus:outline-none dark:border-neutral-600 dark:text-neutral-400 dark:file:rounded dark:file:ml-[-1rem] dark:file:bg-blue-400 dark:file:h-10 dark:file:text-neutral-100 dark:focus:border-primary" />
                </div>

                {/* is Active */}
                <div className="my-2 flex flex-row items-center justify-end">
                    <label htmlFor="active" className="text-gray-500 font-semibold hidden lg:inline-block">
                        {isActive ? 'Actif' : 'Inactif'}
                    </label>
                    <Switch tabIndex={21} id="active" className='w-full h-full' onChange={() => { setIsActive(Boolean(!isActive)) }} checked={Boolean(isActive)} />
                </div>


                <button tabIndex={22} type="submit" className="hidden"></button>
            </form>
        </PopUp>
    );
}

AdminCabinetForm.propTypes = {
    visible: PropTypes.bool.isRequired,
    setVisible: PropTypes.func.isRequired,
    cabinetId: PropTypes.number,
    setCabinet: PropTypes.func.isRequired,
    setToRefresh: PropTypes.func.isRequired
}